import { create } from 'zustand';
import { DateRange } from '../utils/dateUtils';
import { DailyProfitSummary } from '../api/profit';
import { ComparisonType } from '../utils/comparisonUtils';

/**
 * Comparison State Store
 *
 * Manages comparison state for the Profit Monitor's Comparative Analysis feature.
 * - Tracks whether comparison is active and what type is selected
 * - Stores the comparison date range and fetched data
 * - Controls chart overlay visibility
 * - State persists in memory during the user session (no localStorage)
 */

interface ComparisonState {
  // State
  active: boolean; // Whether comparison is currently enabled
  type: ComparisonType; // Type of comparison (previous, lastYear, custom, or null)
  range: DateRange | null; // Date range for comparison period
  data: DailyProfitSummary[] | null; // Fetched comparison data
  overlayVisible: boolean; // Whether chart overlay should be shown

  // Actions
  setComparison: (type: ComparisonType, range: DateRange) => void;
  setComparisonType: (type: 'previous' | 'lastYear') => void;
  clearComparison: () => void;
  setComparisonData: (data: DailyProfitSummary[]) => void;
  toggleOverlay: () => void;
  setOverlayVisible: (visible: boolean) => void;
}

export const useComparisonStore = create<ComparisonState>((set) => ({
  // Initial state - comparison active with 'previous' type by default
  active: true,
  type: 'previous',
  range: null,
  data: null,
  overlayVisible: true, // Default to visible when comparison is activated

  // Activate comparison with specified type and range
  setComparison: (type: ComparisonType, range: DateRange) =>
    set({
      active: true,
      type,
      range,
      data: null // Clear previous data, will be fetched
    }),

  // Toggle between 'previous' and 'lastYear' comparison types
  setComparisonType: (type: 'previous' | 'lastYear') =>
    set({
      type,
      range: null, // Will be recalculated by the hook
      data: null // Clear previous data, will be refetched
    }),

  // Deactivate comparison and clear all state
  clearComparison: () =>
    set({
      active: false,
      type: null,
      range: null,
      data: null
    }),

  // Store fetched comparison data
  setComparisonData: (data: DailyProfitSummary[]) =>
    set({ data }),

  // Toggle chart overlay visibility
  toggleOverlay: () =>
    set((state) => ({ overlayVisible: !state.overlayVisible })),

  // Set chart overlay visibility explicitly
  setOverlayVisible: (visible: boolean) =>
    set({ overlayVisible: visible })
}));

/**
 * Hook to get the current comparison state
 */
export const useComparison = () => {
  const active = useComparisonStore((state) => state.active);
  const type = useComparisonStore((state) => state.type);
  const range = useComparisonStore((state) => state.range);
  const data = useComparisonStore((state) => state.data);
  const overlayVisible = useComparisonStore((state) => state.overlayVisible);

  return { active, type, range, data, overlayVisible };
};

/**
 * Hook to check if comparison is active
 */
export const useComparisonActive = (): boolean => {
  return useComparisonStore((state) => state.active);
};
